import React, { Component, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaBoxOpen,
  FaCheck,
  FaLayerGroup,
  FaStar,
} from 'react-icons/fa';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { richTextToPlainText } from '../utils/richText';
import './DailyProductShowcase.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const resolveMediaUrl = (value) => {
  const source = String(value || '').trim();
  return source.startsWith('/') ? `${API_URL}${source}` : source;
};

const toPrice = (value) => {
  const number = typeof value === 'number'
    ? value
    : Number(String(value || '').replace(/,/g, ''));
  return Number.isFinite(number) ? number : 0;
};

class DailyProductShowcaseBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Daily product showcase was hidden safely:', error);
    }
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const DailyProductShowcaseContent = () => {
  const { t } = useTranslation();
  const { locale, localePath, formatNumber } = useLocale();
  const [showcase, setShowcase] = useState(null);
  const formatPrice = (value, product) => (
    product?.priceAvailable === false
      ? t('products.priceUnavailable')
      : value > 0
        ? `${formatNumber(value)} ${product?.currencyLabel || locale.currencyLabel}`
        : t('showcase.free')
  );

  const loadShowcase = useCallback(async () => {
    try {
      const response = await api.get(`/api/products/showcase?locale=${encodeURIComponent(locale.code)}`);
      const payload = response?.success ? response.data : null;
      setShowcase(payload?.enabled && payload?.product ? payload : null);
    } catch (_) {
      setShowcase(null);
    }
  }, [locale.code]);

  useEffect(() => {
    loadShowcase();
    const reloadWhenVisible = () => {
      if (document.visibilityState === 'visible') loadShowcase();
    };
    window.addEventListener('focus', loadShowcase);
    document.addEventListener('visibilitychange', reloadWhenVisible);
    return () => {
      window.removeEventListener('focus', loadShowcase);
      document.removeEventListener('visibilitychange', reloadWhenVisible);
    };
  }, [loadShowcase]);

  useEffect(() => {
    const nextChange = new Date(showcase?.schedule?.nextChangeAt || '').getTime();
    if (!Number.isFinite(nextChange)) return undefined;
    const delay = Math.max(1000, Math.min(2147483647, nextChange - Date.now() + 1500));
    const timer = window.setTimeout(loadShowcase, delay);
    return () => window.clearTimeout(timer);
  }, [loadShowcase, showcase?.schedule?.nextChangeAt]);

  const product = showcase?.product;
  const schedule = showcase?.schedule;
  const presentation = useMemo(() => {
    if (!product || !schedule) return null;
    const price = toPrice(product.price);
    const discountPrice = toPrice(product.discountPrice);
    const hasDiscount = discountPrice > 0 && discountPrice < price;
    const imageUrl = resolveMediaUrl(
      product.detailImageUrl || product.cardImageUrl || product.imageUrl
    );
    const description = richTextToPlainText(product.description).slice(0, 230);
    const features = Array.isArray(product.features)
      ? product.features.filter(Boolean).slice(0, 4)
      : [];
    return {
      price,
      discountPrice,
      hasDiscount,
      discountPercent: hasDiscount ? Math.round((1 - discountPrice / price) * 100) : 0,
      imageUrl,
      description,
      features,
      route: `/products/${product.slug || product._id}`,
    };
  }, [product, schedule]);

  if (!product || !presentation) return null;

  return (
    <section className="daily-product-showcase" aria-labelledby="daily-product-showcase-title">
      <div className="daily-product-showcase__shell">
        <header className="daily-product-showcase__heading">
          <span className="daily-product-showcase__eyebrow">
            <FaLayerGroup aria-hidden="true" /> {t('showcase.eyebrow')}
          </span>
          <h2 id="daily-product-showcase-title">
            {t('showcase.title')}
          </h2>
        </header>

        <article className="daily-product-showcase__card">
          <div className="daily-product-showcase__copy">
            <div className="daily-product-showcase__meta">
              <span className="daily-product-showcase__today-badge">
                <span aria-hidden="true" /> {t('showcase.today')}
              </span>
              {product.category && <span>{product.category}</span>}
              {product.version && <span>{t('showcase.version', { version: product.version })}</span>}
            </div>

            <h3>{product.name}</h3>
            {presentation.description && <p>{presentation.description}</p>}

            {presentation.features.length > 0 && (
              <ul className="daily-product-showcase__features">
                {presentation.features.map((feature, index) => (
                  <li key={`${feature}-${index}`}><FaCheck aria-hidden="true" /> {feature}</li>
                ))}
              </ul>
            )}

            <div className="daily-product-showcase__purchase">
              <div className="daily-product-showcase__price-box">
                <span className="daily-product-showcase__price-label">{t('showcase.price')}</span>
                <div className="daily-product-showcase__price-row">
                  <div className="daily-product-showcase__price">
                    {presentation.hasDiscount && (
                      <span className="daily-product-showcase__old-price">
                        {formatPrice(presentation.price, product)}
                      </span>
                    )}
                    <strong>{formatPrice(presentation.hasDiscount ? presentation.discountPrice : presentation.price, product)}</strong>
                  </div>
                  {presentation.hasDiscount && (
                    <span className="daily-product-showcase__discount">
                      <strong>{formatNumber(presentation.discountPercent)}٪</strong>
                      <small>{t('showcase.discount')}</small>
                    </span>
                  )}
                </div>
              </div>

              <div className="daily-product-showcase__actions">
                <Link className="daily-product-showcase__primary-action" to={localePath(presentation.route)}>
                  {t('showcase.view')} <FaArrowLeft aria-hidden="true" />
                </Link>
                <Link className="daily-product-showcase__secondary-action" to={localePath('/products')}>
                  {t('showcase.all')}
                </Link>
              </div>
            </div>
          </div>

          <div className="daily-product-showcase__visual">
            <div className="daily-product-showcase__visual-grid" aria-hidden="true" />
            <div className="daily-product-showcase__visual-ring" aria-hidden="true" />

            <div className="daily-product-showcase__product-display">
              <div className="daily-product-showcase__image-stage">
                <div className="daily-product-showcase__image-fallback" aria-hidden="true">
                  <FaBoxOpen />
                </div>
                {presentation.imageUrl && (
                  <img
                    src={presentation.imageUrl}
                    alt={product.name}
                    loading="lazy"
                    onError={(event) => { event.currentTarget.style.display = 'none'; }}
                  />
                )}
              </div>
            </div>

            <div className="daily-product-showcase__visual-note">
              <span aria-hidden="true" />
              <div>
                <small>{t('showcase.selected')}</small>
                <strong>{t('showcase.ready')}</strong>
              </div>
              {Number(product.rating) > 0 && (
                <span className="daily-product-showcase__rating">
                  <FaStar aria-hidden="true" /> {formatNumber(Number(product.rating))}
                </span>
              )}
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

const DailyProductShowcase = () => (
  <DailyProductShowcaseBoundary>
    <DailyProductShowcaseContent />
  </DailyProductShowcaseBoundary>
);

export default DailyProductShowcase;
